/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Map as ImmutableMap } from 'immutable';
import {
  Historical,
  CallEvent,
  UserPresenceEvent,
  QueuePresenceEvent,
  QueueCallState,
  CallbackState,
  UserState,
  CallUser,
  Delta,
} from './QueueLeaderboard';

import { DispositionEvent } from './NEW/NewDisposition';
import { HistoricalIvr, IvrState } from './IvrSummary/IvrHud';
import { CallSummaryEvent } from './Hud';

interface MarkAsCalledMessage {
  time: string;
  callbackNumber: string;
}
interface BatchMessage {
  callbackState: CallbackState[];
  leaderboardDelta: object[];
  queueCallState: QueueCallState[];
  userState: UserState[];
  myCalls?: CallUser[];
  acwEvent?: {
    duration: string;
    label: string;
  }[];
  disposition: DispositionEvent[];
  ivrCallState: IvrState[];
  ivrDelta: Delta[];
}

interface DeletedMessage {
  msgId: string;
  time: string;
}

interface CodeItem {
  code: string;
  description: string;
  group: string;
  oldDescription?: string;
}
interface CodeEvent {
  deletes: CodeItem[];
  updates: CodeItem[];
}

interface PresenceListEvent {
  deletes: {
    id: string;
    table: string;
  }[];
  updates: {
    id: string;
    category: string;
    status: string;
    table: string;
    duration?: string;
    autoSwitchback?: boolean;
  }[];
  orders: {
    id: string;
    order: string[];
  }[];
  acws: {
    duration: any;
    acw: any;
    id: string;
    label: string;
  }[];
}

interface UpdateVipEvent {
  id: string;
  name: string;
  number: string;
}

interface DeleteVipEvent {
  id: string;
}

interface QueueUpdateEvent {
  id: string;
  userId: string;
  acceptCurrentQueueCalls: string;
}

interface WebSocketMessage {
  alive?: boolean;
  payload: string;
  time: number;
  data:
    | MarkAsCalledMessage
    | BatchMessage
    | DeletedMessage
    | UserPresenceEvent
    | CodeEvent
    | PresenceListEvent
    | UpdateVipEvent
    | DeleteVipEvent
    | QueueUpdateEvent;
}

const Websocket = React.memo(
  ({
    setCodeMap,
    idToken,
    setHistoricalQueues,
    setHistoricalUsers,
    origin,
    setTables,
    setCodes,
    setRules,
    rcId,
    forwardAddress,
    setOffset,
    setHistoricals,
    setPresenceEvents,
    setQueuePresenceEvents,
    setQueueCallStates,
    setCallbackStates,
    setUserStates,
    leaderboardUrl,
    setLeaderboardDeltas,
    setViplist,
    setMarkedAsCalledBack,
    setIvrLists,
    setMyCalls,
    setDeletedVoiceMails,
    setCallbacks,
    setLiveCalls,
    setSmsData,
    setHistoricalAgents,
    setHistoricalIvr,
    setIvrDeltas,
    setIvrStates,
    organizationId,
  }: {
    setCodeMap: Function;
    setSmsData: Function;
    setCallbacks: any;
    setDeletedVoiceMails: any;
    idToken: string;
    setHistoricalQueues: any;
    setHistoricalUsers: any;
    origin: string;
    setTables: any;
    setCodes: any;
    setRules: any;
    rcId: string;
    organizationId: string;
    forwardAddress: string;
    setOffset: any;
    setHistoricals: any;
    setPresenceEvents: any;
    setQueuePresenceEvents: any;
    setQueueCallStates: any;
    setCallbackStates: any;
    setUserStates: React.Dispatch<React.SetStateAction<ImmutableMap<string, UserState>>>;
    leaderboardUrl: string | undefined;
    setLeaderboardDeltas: any;

    setViplist: any;
    setMarkedAsCalledBack: any;
    setIvrLists: any;
    setMyCalls: React.Dispatch<React.SetStateAction<Map<string, CallUser> | undefined>>;
    setHistoricalAgents: Function;
    setLiveCalls: React.Dispatch<React.SetStateAction<Map<string, CallUser[]> | undefined>>;
    setHistoricalIvr: React.Dispatch<React.SetStateAction<ImmutableMap<string, HistoricalIvr>>>;
    setIvrDeltas: React.Dispatch<React.SetStateAction<Array<Delta>>>;
    setIvrStates: React.Dispatch<React.SetStateAction<Array<IvrState>>>;
  }) => {
    const leaderboardBuffer = useRef<object[]>([]);
    const queueStateBuffer = useRef<object[]>([]);
    const userStateBuffer = useRef<UserState[]>([]);
    const presenceBuffer = useRef<UserPresenceEvent[]>([]);
    const queueUpdateBuffer = useRef<QueueUpdateEvent[]>([]);
    const dispositionBuffer = useRef<DispositionEvent[]>([]);
    const socketBuffer = useRef<{ data: string }[]>([]);
    const ivrStatesBuffer = useRef<IvrState[]>([]);
    const ivrSummaryBuffer = useRef<Delta[]>([]);
    const pingTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastTime = useRef<number | null>(null);
    const historicalReady = useRef(false);
    const [connected, setConnected] = useState<boolean>(false);
    const latestTimestamps = useRef<Map<string, number>>(new Map());

    useEffect(() => {
      if (connected) {
        return;
      }
      console.log('Getting account summary');
      (async () => {
        const myUrl = new URL(forwardAddress.replace('/shadow-agent', '/account'));
        try {
          const data = (await fetch(myUrl.toString(), {
            headers: {
              authorization: idToken,
            },
          }).then((res) => {
            if (!res.ok) {
              throw new Error('request error');
            }

            return res.json();
          })) as Historical;

          // setEvents([]);
          // setEventData([]);
          setTables(data.table || []);
          setCodes(data.code || []);
          setRules(data.rule || []);
          setIvrLists(
            data.ivr.map((item) => ({
              ...item,
              disabled: !!data.queue.find((temp) => temp.ivrId === item.id),
            })) || [],
          );
          setHistoricalIvr(
            ImmutableMap<string, HistoricalIvr>(
              data.ivr.reduce((acc: { [key: string]: HistoricalIvr }, myIvr: any) => {
                const id = myIvr.id || myIvr.SortKey;
                const isAir = myIvr.PartitionKey.startsWith('AIR');
                acc[id] = { id, isAir, ...myIvr };
                return acc;
              }, {}),
            ),
          );
          setHistoricalQueues(
            ImmutableMap(
              data.queue.reduce((acc: { [key: string]: any }, myQueue: any) => {
                const users = (myQueue.users || []).map(
                  (user: { id: string; acceptCurrentQueueCalls: boolean; acceptQueueCalls?: boolean }) => {
                    const temp = data.user.find((historicalUser) => historicalUser.id === user.id);

                    return {
                      ...user,
                      acceptQueueCalls:
                        temp?.display?.startsWith('AVL:') && temp.lastDndStatus !== 'DoNotAcceptDepartmentCalls',
                    };
                  },
                );

                acc[myQueue.id] = {
                  serviceLevelSettings: {
                    slaGoal: 80,
                    slaThresholdSeconds: 20,
                    includeAbandonedCalls: true,
                    abandonedThresholdSeconds: 5,
                  },
                  ...myQueue,
                  users,
                };

                return acc;
              }, {}),
            ),
          );

          const reduced = data.user.reduce<Record<string, any>>((acc, myHistoricalUser: any) => {
            const userId = myHistoricalUser.id; // Ensure the `id` exists and is unique
            acc[userId] = {
              ...myHistoricalUser,
              department: myHistoricalUser.department || '{Not defined}',
            };
            return acc;
          }, {});
          const immut = ImmutableMap(reduced);
          setHistoricalUsers(immut);
          setOffset(data.currentTime - new Date().getTime());
          setConnected(true);
          setHistoricals((oldHistorical: Array<Historical>) => oldHistorical.concat([data]));
        } catch (err) {
          console.log(err);
        }
      })();
    }, [origin, connected]);

    const loadHistorical = async ({ since, reconnect = false }: { since?: Date; reconnect?: boolean }) => {
      const startTime =
        since ||
        (() => {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          return d;
        })();

      let agentData: any[] = [];
      let callbackData: any[] = [];
      let markedData: any[] = [];
      let deletedVmData: any[] = [];
      let myCalls: any[] = [];
      let first = true;
      const getSummary = async (lastKey = null) => {
        let url = `${leaderboardUrl}?startTime=${startTime.toISOString()}`;
        if (lastKey) {
          url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
        }

        const res = await fetch(url, {
          headers: {
            Authorization: idToken,
          },
        });
        const data = await res.json();

        // These are fine as is, even on reconnect
        leaderboardBuffer.current.push(...data.users);
        if (first) {
          queueStateBuffer.current.push(...data.queueCallState);
          ivrStatesBuffer.current.push(...data.ivrStates);
        }
        userStateBuffer.current.push(...data.userStates);
        dispositionBuffer.current.push(...data.disposition);
        ivrSummaryBuffer.current.push(...data.ivrSummary);

        // These are merged differently depending on reconnect
        agentData = agentData.concat(data.agents);
        callbackData = callbackData.concat(data.callback);
        markedData = markedData.concat(data.markedAsCalled);
        deletedVmData = deletedVmData.concat(data.deletedVoiceMails);
        myCalls = myCalls.concat(data.myCalls);

        processTriggerRef.current();

        if (data.lastKey) {
          first = false;
          await getSummary(data.lastKey);
        }
      };

      await getSummary();

      // Finalize and merge into state
      if (reconnect) {
        // AGENTS: Do not modify
        // CALLBACKS, MARKED, DELETED_VM: Append
        setCallbacks((prev: any) => [...prev, ...callbackData]);
        setMarkedAsCalledBack((prev: any) => [...prev, ...markedData]);
        setDeletedVoiceMails((prev: any) => {
          const merged = new Set(prev);
          deletedVmData.forEach((vm) => merged.add(vm));
          return merged;
        });

        // MY CALLS: merge into map by callId
        setMyCalls((prev) => {
          const merged = new Map(prev);
          myCalls.forEach((call) => merged.set(call.callId, call));
          return merged;
        });
      } else {
        // Initial full set
        setHistoricalAgents(agentData);
        setCallbacks(callbackData);
        setMarkedAsCalledBack(markedData);
        setDeletedVoiceMails(new Set(deletedVmData));
        setMyCalls(
          myCalls.reduce((map, call) => {
            map.set(call.callId, call);
            return map;
          }, new Map()),
        );
      }

      historicalReady.current = true;

      console.log('Historical loaded, replaying sockets', socketBuffer.current.length);
      socketBuffer.current.splice(0).forEach((item) => cb(item));
      setTimeout(() => {
        if (socketBuffer.current.length) {
          socketBuffer.current.forEach((item) => cb(item));
        }
      }, 2000);
    };

    // Load historical on page load
    useEffect(() => {
      loadHistorical({});
    }, []);

    const getEventKey = (event: QueueCallState) => {
      if (event.userId) {
        return `${event.queueId}#${event.userId}#${event.callId}`;
      }
      return `${event.queueId}#${event.callId}`;
    };

    const processQueueState = (events: QueueCallState[]) => {
      const filteredEvents = events.filter((event) => {
        const key = getEventKey(event);
        const currentTimestamp = latestTimestamps.current.get(key);

        // If we don't have a record of this event, or the new timestamp is more recent
        if (currentTimestamp === undefined || new Date(event.time).getTime() > currentTimestamp) {
          latestTimestamps.current.set(key, new Date(event.time).getTime());
          return true;
        }
        return false;
      });

      if (filteredEvents.length > 0) {
        queueStateBuffer.current.push(...filteredEvents);
      }
    };

    const cb = useCallback((event: { data: string }) => {
      if (!historicalReady.current) {
        socketBuffer.current.push(event);
        return;
      }
      const item: WebSocketMessage = JSON.parse(event.data);
      lastTime.current = item.time ? item.time : lastTime.current;
      // eslint-disable-next-line no-case-declarations
      const mixedMessageBody: any = item.data;
      if (mixedMessageBody.telephonyMessages?.length) {
        // eslint-disable-next-line no-restricted-syntax
        for (const messageObject of mixedMessageBody.telephonyMessages) {
          const msg: BatchMessage = messageObject.message;
          if (msg.ivrCallState.length > 0) {
            ivrStatesBuffer.current.push(...msg.ivrCallState);
          }

          if (msg.ivrDelta.length > 0) {
            ivrSummaryBuffer.current.push(...msg.ivrDelta);
          }

          if (msg.userState) {
            userStateBuffer.current.push(...msg.userState);
          }

          if (msg.callbackState.length > 0) {
            setCallbackStates((oldCallbackStates: Array<CallbackState>) => oldCallbackStates.concat(msg.callbackState));
          }

          if (msg.queueCallState.length > 0) {
            processQueueState(msg.queueCallState);
          }

          if (msg.leaderboardDelta.length > 0) {
            leaderboardBuffer.current.push(...msg.leaderboardDelta);
          }

          if (msg.disposition) {
            dispositionBuffer.current.push(...msg.disposition);
          }
        }
      }

      if (mixedMessageBody.presenceMessages?.length) {
        // eslint-disable-next-line no-restricted-syntax
        for (const messageObject of mixedMessageBody.presenceMessages) {
          const payload = messageObject.message;
          presenceBuffer.current.push(payload);
        }
      }

      if (mixedMessageBody.queuePresenceMessages?.length) {
        // eslint-disable-next-line no-restricted-syntax
        for (const messageObject of mixedMessageBody.queuePresenceMessages) {
          const queuePayload = messageObject.message;
          queueUpdateBuffer.current.push(queuePayload);
        }
      }

      if (mixedMessageBody.messages?.length) {
        // eslint-disable-next-line no-restricted-syntax
        for (const message of mixedMessageBody.messages) {
          const { payload } = message;
          switch (payload) {
            case 'mark-as-called':
              // eslint-disable-next-line no-case-declarations
              const markedMsg = message.message as MarkAsCalledMessage;
              setMarkedAsCalledBack((old: any) => old.concat([markedMsg]));
              break;
            case 'shared-voicemail-deleted':
              // eslint-disable-next-line no-case-declarations
              const vmDelMsg = message.message as DeletedMessage;
              setDeletedVoiceMails((old: Set<string>) => new Set(old).add(`${vmDelMsg.msgId}`));
              break;
            case 'codes':
              // eslint-disable-next-line no-case-declarations
              const codePayload = message.message as CodeEvent;

              // individual-code-deleted
              if (codePayload.deletes.length > 0) {
                codePayload.deletes.forEach((deleted) => {
                  setCodes((oldCodes: any) => {
                    const newCodes = oldCodes.slice();
                    const oldCode = newCodes.find((code: any) => code.id === deleted.group);
                    if (oldCode) {
                      const itemIndex = oldCode.items.findIndex((i: any) => i.id.split(':')[0] === deleted.code);

                      if (itemIndex !== -1) {
                        oldCode.items.splice(itemIndex, 1);
                      }
                    }
                    return newCodes;
                  });
                });
              }

              // individual-code-updated
              if (codePayload.updates.length > 0) {
                codePayload.updates.forEach((updated) => {
                  setCodes((oldCodes: any) => {
                    const newCodes = oldCodes.slice();
                    const oldCode = newCodes.find((code: any) => code.id === updated.group);
                    if (oldCode) {
                      const itemIndex = oldCode.items.findIndex((i: any) => i.id.split(':')[0] === updated.code);
                      if (itemIndex !== -1) {
                        oldCode.items[itemIndex] = {
                          id: `${updated.code}:${updated.description}`,
                          code: updated.group,
                        };
                      } else {
                        oldCode.items.push({
                          id: `${updated.code}:${updated.description}`,
                          code: updated.group,
                        });
                      }
                    }
                    return newCodes;
                  });
                });
              }

              break;
            case 'presence-list':
              // eslint-disable-next-line no-case-declarations
              const listPayload = message.message as PresenceListEvent;
              console.log(item);
              listPayload.updates.forEach((update) => {
                setTables((oldTables: any) => {
                  const newTables = oldTables.slice();
                  const oldTable = newTables.find((table: any) => table.id === update.table);
                  if (oldTable) {
                    const itemIndex = oldTable.items.findIndex((i: any) => i.id === update.id);

                    if (itemIndex !== -1) {
                      oldTable.items[itemIndex] = update;
                    } else {
                      oldTable.items.push(update);
                    }
                  }

                  return newTables;
                });
              });

              listPayload.deletes.forEach((update) => {
                setTables((oldTables: any) => {
                  const newTables = oldTables.slice();
                  const oldTable = newTables.find((table: any) => table.id === update.table);
                  if (oldTable) {
                    const itemIndex = oldTable.items.findIndex((i: any) => i.id === update.id);

                    if (itemIndex !== -1) {
                      oldTable.items.splice(itemIndex, 1);
                    }
                  }

                  return newTables;
                });
              });

              listPayload.orders.forEach((update) => {
                setTables((oldTables: any) => {
                  const newTables = oldTables.map((table: any) => {
                    if (table.id === update.id.toString()) {
                      return {
                        ...table,
                        ...update,
                      };
                    }
                    return table;
                  });
                  return newTables;
                });
              });

              listPayload.acws.forEach((update) => {
                setHistoricalQueues((oldQueues: any) => {
                  if (!oldQueues) {
                    return oldQueues;
                  }

                  // Update the specific queue in the Immutable Map
                  return oldQueues.update(update.id, (queue: any) => {
                    if (!queue) {
                      return queue;
                    }

                    if (update.acw) {
                      if (update.duration) {
                        return {
                          ...queue,
                          acw: {
                            duration: update.duration,
                            label: update.label,
                          },
                        };
                      }
                      return {
                        ...queue,
                        acw: {
                          ...queue.acw,
                          label: update.label,
                        },
                      };
                    }

                    // Remove the `acw` field if not present in the update
                    const { acw, ...restQueue } = queue;
                    return restQueue;
                  });
                });
              });

              break;

            case 'call-recarding-ready':
              // eslint-disable-next-line no-case-declarations
              const recordingReadyPayload = message.message as { callId: string; recordingId: string; userId: string };
              break;
            case 'call-recording-not-ready':
              // eslint-disable-next-line no-case-declarations
              const recordingNotReadyPayload = message.message as {
                callId: string;
                recordingId: string;
                userId: string;
              };
              break;
            case 'insight-ready':
              // eslint-disable-next-line no-case-declarations
              const insightReadyPayload = message.message as {
                callId: string;
                sentimentScore?: number;
                score?: string;
                userId: string;
              };
              break;
            default:
              break;
          }
        }
      }
      processTriggerRef.current();
    }, []);

    const redisPingTimeout = useRef<NodeJS.Timeout | null>(null);
    const redisPongTimeout = useRef<NodeJS.Timeout | null>(null);
    const pongGracePeriod = 45000; // 45 seconds max without a pong

    // Redis socket
    useEffect(() => {
      let socket: WebSocket | null = null;
      let reconnectTimeout: any = null;
      let reconnectAttempts = 0;
      let isCleanedUp = false;
      let hasConnectedOnce = false;

      const softCleanup = () => {
        if (redisPingTimeout.current) {
          clearTimeout(redisPingTimeout.current);
          redisPingTimeout.current = null;
        }
        if (redisPongTimeout.current) {
          clearTimeout(redisPongTimeout.current);
          redisPongTimeout.current = null;
        }
        clearTimeout(reconnectTimeout);
      };

      const cleanup = () => {
        isCleanedUp = true;
        softCleanup();
        if (socket) {
          socket.removeEventListener('open', open);
          socket.removeEventListener('message', handleMessage);
          socket.removeEventListener('close', handleClose);
          socket.removeEventListener('error', handleError);
          try {
            if (socket.readyState === WebSocket.OPEN) {
              socket.close();
            }
          } catch (e) {
            console.error('Error closing socket:', e);
          }
          socket = null;
        }
      };

      const handleClose = () => {
        if (isCleanedUp) return;
        console.log('WebSocket closed - reconnecting...');
        softCleanup();

        // Exponential backoff, no jitter.
        const maxAttempts = 3;
        const maxDelay = 30000;
        const baseDelay = Math.min(1000 * 2 ** reconnectAttempts, maxDelay);

        if (reconnectAttempts < maxAttempts) {
          reconnectTimeout = setTimeout(() => {
            if (!isCleanedUp) initialize();
          }, baseDelay);
          reconnectAttempts += 1;
        } else {
          // Refresh page, token is likely expired here.
          window.location.reload();
        }
      };

      const startPing = (isFirst = true) => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;

        if (!isFirst) {
          try {
            socket.send(JSON.stringify({ action: 'ping' }));
          } catch (err) {
            console.warn('Failed to send ping:', err);
          }

          // Set pong timeout
          if (redisPongTimeout.current) {
            clearTimeout(redisPongTimeout.current);
          }
          redisPongTimeout.current = setTimeout(() => {
            console.warn('No pong received in time — closing socket.');
            socket?.close(); // triggers reconnect
          }, pongGracePeriod);
        }

        if (redisPingTimeout.current) {
          clearTimeout(redisPingTimeout.current);
          redisPingTimeout.current = null;
        }

        // Schedule next ping
        redisPingTimeout.current = setTimeout(() => startPing(false), 15000);
      };

      const handleError = (err: Event) => {
        console.error('WebSocket error:', err);
      };

      const handleMessage = (event: { data: string }) => {
        const item: WebSocketMessage = JSON.parse(event.data);

        if (item.alive === true) {
          // Got pong — clear pong timeout
          if (redisPongTimeout.current) {
            clearTimeout(redisPongTimeout.current);
            redisPongTimeout.current = null;
          }
        } else {
          cb(event);
        }
      };

      const open = async () => {
        if (isCleanedUp) return;
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        reconnectAttempts = 0;
        startPing();

        if (hasConnectedOnce) {
          const grabTime = lastTime.current || new Date().getTime();
          console.log('Socket reconnected: Fetching missed message since', new Date(grabTime).toISOString());
          historicalReady.current = false;
          await loadHistorical({
            since: new Date(grabTime),
            reconnect: true,
          });
          setConnected(false);
        } else {
          hasConnectedOnce = true;
        }
      };

      const initialize = () => {
        if (isCleanedUp) return;

        if (socket) {
          socket.removeEventListener('open', open);
          socket.removeEventListener('message', handleMessage);
          socket.removeEventListener('close', handleClose);
          socket.removeEventListener('error', handleError);
          try {
            socket.close();
          } catch (e) {
            console.error('Error closing socket:', e);
          }
        }

        try {
          socket = new WebSocket(
            `${(process.env.REACT_APP_NEW_SOCKET_URL || '').replace('https', 'wss')}/?jwt=${idToken}`,
          );
          socket.addEventListener('open', open);
          socket.addEventListener('message', handleMessage);
          socket.addEventListener('close', handleClose);
          socket.addEventListener('error', handleError);
        } catch (e) {
          console.error('WebSocket initialization error:', e);
          handleClose();
        }
      };

      initialize();

      // eslint-disable-next-line consistent-return
      return cleanup;
    }, []);

    const isProcessingRef = useRef(false);
    const lastProcessedRef = useRef(Date.now());
    const processTimeoutRef = useRef<any>(null);
    const processTriggerRef = useRef<() => void>(() => {});
    const pendingTriggerRef = useRef(false);

    useEffect(() => {
      const triggerProcessing = () => {
        if (isProcessingRef.current) {
          pendingTriggerRef.current = true;
          return;
        }

        if (processTimeoutRef.current) return;
        processTimeoutRef.current = setTimeout(() => {
          isProcessingRef.current = true;
          (async () => {
            try {
              const lbBufferLength = leaderboardBuffer.current.length;
              const userStateBufferLength = userStateBuffer.current.length;
              const queueStateBufferLength = queueStateBuffer.current.length;
              const presenceBufferLength = presenceBuffer.current.length;
              const queueUpdateLength = queueUpdateBuffer.current.length;
              const dispositionUpdateLength = dispositionBuffer.current.length;
              const ivrStatesBufferLength = ivrStatesBuffer.current.length;
              const ivrSummaryBufferLength = ivrSummaryBuffer.current.length;
              if (ivrStatesBufferLength > 0) {
                const batch = ivrStatesBuffer.current.splice(0, ivrStatesBufferLength);
                setIvrStates(batch);
              }

              if (ivrSummaryBufferLength > 0) {
                const batch = ivrSummaryBuffer.current.splice(0, ivrSummaryBufferLength);
                setIvrDeltas(batch);
              }

              if (dispositionUpdateLength > 0) {
                const batch = dispositionBuffer.current
                  .splice(0, dispositionUpdateLength)
                  .filter((item) => item.userId === rcId);
                setCodeMap((oldCodeMap: ImmutableMap<string, DispositionEvent>) => {
                  let updatedMap = oldCodeMap;

                  batch.forEach((item) => {
                    updatedMap = updatedMap.update(item.id, (existing = {} as DispositionEvent) => ({
                      ...existing,
                      ...item,
                    }));
                  });

                  return updatedMap;
                });
              }

              if (lbBufferLength > 0) {
                const batch = leaderboardBuffer.current.splice(0, lbBufferLength);
                setLeaderboardDeltas(batch);
              }

              if (userStateBufferLength > 0) {
                const batch = userStateBuffer.current.splice(0, userStateBufferLength);

                setUserStates((oldStates: ImmutableMap<string, UserState>) => {
                  const updatedStates = batch.reduce((acc, payload) => {
                    const oldState = oldStates.get(payload.userId);
                    const isNewer = !oldState || payload.time > oldState.time;

                    if (isNewer) {
                      const updatedState = {
                        ...oldState,
                        ...payload,
                      };
                      return acc.set(payload.userId, updatedState);
                    }
                    return acc;
                  }, oldStates);

                  return updatedStates;
                });
              }

              if (queueStateBufferLength > 0) {
                const batch = [...queueStateBuffer.current.splice(0, queueStateBufferLength)];
                setQueueCallStates(batch);
              }

              if (presenceBufferLength > 0) {
                const batch = presenceBuffer.current.splice(0, presenceBufferLength);
                setPresenceEvents(batch);

                // Update historical users in one atomic operation
                setHistoricalUsers((oldUsers: ImmutableMap<string, any>) => {
                  if (!oldUsers) return oldUsers;

                  const updatedUsers = batch.reduce((acc, payload) => {
                    const updatedUser = {
                      ...oldUsers.get(payload.id), // Existing user data
                      ...payload, // Updated data from payload
                    };
                    return acc.set(payload.id, updatedUser);
                  }, oldUsers);

                  return updatedUsers;
                });
              }

              if (queueUpdateLength > 0) {
                const batch = queueUpdateBuffer.current.splice(0, queueUpdateLength);
                setQueuePresenceEvents(batch);

                setHistoricalQueues((oldQueues: ImmutableMap<string, any>) => {
                  if (!oldQueues) return oldQueues;

                  const updatedQueues = batch.reduce((acc, payload) => {
                    const oldQueue = oldQueues.get(payload.id);
                    if (!oldQueue) return acc;

                    // Clone the queue and users array
                    const newUsers = oldQueue.users.map((user: any) =>
                      user.id === payload.userId
                        ? {
                            ...user,
                            acceptCurrentQueueCalls: payload.acceptCurrentQueueCalls,
                          }
                        : user,
                    );

                    const newQueue = { ...oldQueue, users: newUsers }; // New object reference
                    return acc.set(payload.id, newQueue); // Immutable.js will now detect a change
                  }, oldQueues);

                  return updatedQueues.equals(oldQueues) ? oldQueues : updatedQueues; // Prevent unnecessary updates
                });
              }
            } catch (error) {
              console.error('Error processing buffers:', error);
            } finally {
              lastProcessedRef.current = Date.now();
              isProcessingRef.current = false;
              processTimeoutRef.current = null;
              if (pendingTriggerRef.current) {
                pendingTriggerRef.current = false;
                triggerProcessing();
              }
            }
          })();
        }, 1500);
      };

      processTriggerRef.current = triggerProcessing;

      return () => {
        if (processTimeoutRef.current) clearTimeout(processTimeoutRef.current);
        isProcessingRef.current = false;
      };
    }, []);

    useEffect(() => {
        const todayStart = new Date(new Date().getTime());
        todayStart.setHours(0, 0, 0, 0);

        const getSMSData = async () => {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/sms`, {
            headers: {
              Authorization: idToken,
            },
          });
          return res.json();
        };

        getSMSData()
          .then(({ users, extensions }) => {
            setSmsData({
              users,
              extensions,
            });
          })
          .catch((error) => {
            console.error('Error fetching SMS data:', error);
          });
    }, []);

    return <></>;
  },
);

export default Websocket;
